import { MutableRefObject, useCallback, useMemo, useRef, useState } from 'react';
import {
  BimEntityProperties,
  BimMeasureMode,
  BimMeasurement,
  BimProjectionMode,
  BimSectionPlane,
  BimSelection,
  BimTreeNode,
  BimViewerHandle,
} from 'Data/interfaces/BimViewer/IBimViewerPort';

// Seleção corrente do viewer. `ids` é o conjunto sobre o qual isolar/ocultar
// agem: um elemento (clique no 3D ou numa folha da árvore) ou tudo o que está
// sob um nó da árvore (pavimento, grupo de tipo).
export interface IBimSelection {
  ids: number[];
  /** Elemento cujas propriedades o painel mostra; null em nó de contêiner. */
  primaryId: number | null;
  /** Nós da árvore a destacar. O primeiro é a linha que a árvore revela. */
  treeKeys: string[];
}

// Teto da agregação de propriedades. Um nó de árvore pode cobrir milhares de
// elementos; agregar tudo travaria a UI por um painel que ninguém leria inteiro.
const MAX_AGGREGATED_PROPERTIES = 200;

const DEFAULT_SECTION: BimSectionPlane = {
  axis: 'down',
  position: 50,
  enabled: false,
  flipped: false,
};

/** Todos os elementos abaixo de um nó (folhas `type-group` carregam os ids), peças inclusive. */
const collectIds = (node: BimTreeNode, out: number[] = []): number[] => {
  if (node.expressIds) { out.push(...node.expressIds); }
  if (node.parts) {
    for (const parts of Object.values(node.parts)) { out.push(...parts); }
  }
  for (const child of node.children) { collectIds(child, out); }
  return out;
};

/** Folha de elemento: ele e as peças, que a árvore materializa como filhas. */
const elementIds = (node: BimTreeNode): number[] =>
  [node, ...node.children]
    .map((item) => item.expressId)
    .filter((id): id is number => id !== undefined);

/**
 * Quantos elementos de cada nó estão ocultos, por chave. Uma passada só na
 * árvore inteira a cada mudança de visibilidade — a alternativa (cada linha da
 * árvore contar os seus ids ao renderizar) refaz o mesmo trabalho a cada frame,
 * e um pavimento sozinho tem dezenas de milhares de ids.
 */
export const countHidden = (nodes: BimTreeNode[], hidden: Set<number>): Map<string, number> => {
  const counts = new Map<string, number>();
  const walk = (node: BimTreeNode): number => {
    let count = 0;
    if (node.expressIds) {
      for (const id of node.expressIds) { if (hidden.has(id)) { count++; } }
    }
    for (const child of node.children) { count += walk(child); }
    counts.set(node.key, count);
    return count;
  };
  nodes.forEach(walk);
  return counts;
};

/**
 * Para o caminho 3D → árvore: expressId → chave do `type-group` que o contém, e
 * peça → elemento dono (a folha que precisa abrir para a peça aparecer).
 */
const indexElements = (nodes: BimTreeNode[]) => {
  const groupOf = new Map<number, string>();
  const ownerOf = new Map<number, number>();
  const walk = (node: BimTreeNode) => {
    if (node.expressIds) {
      for (const id of node.expressIds) { groupOf.set(id, node.key); }
    }
    if (node.parts) {
      for (const [owner, parts] of Object.entries(node.parts)) {
        for (const part of parts) {
          groupOf.set(part, node.key);
          ownerOf.set(part, Number(owner));
        }
      }
    }
    node.children.forEach(walk);
  };
  nodes.forEach(walk);
  return { groupOf, ownerOf };
};

/** Chave da folha de elemento — as folhas nascem sob demanda nos grupos de tipo. */
export const elementTreeKey = (expressId: number): string => `e:${expressId}`;

/** Caminho de chaves da raiz até o nó alvo — o que precisa ser expandido. */
const pathTo = (nodes: BimTreeNode[], targetKey: string): string[] => {
  const walk = (node: BimTreeNode, trail: string[]): string[] | null => {
    if (node.key === targetKey) { return trail; }
    for (const child of node.children) {
      const hit = walk(child, [...trail, node.key]);
      if (hit) { return hit; }
    }
    return null;
  };
  for (const root of nodes) {
    const hit = walk(root, []);
    if (hit) { return hit; }
  }
  return [];
};

interface IUseBim3DToolsParams {
  handleRef: MutableRefObject<BimViewerHandle | null>;
  /**
   * Modelo a que estas ferramentas se referem, na cena federada (uma instância
   * por modelo, uma árvore por modelo). Ausente = viewer de arquivo único, onde
   * a cena tem um modelo só e o índice não faz sentido.
   */
  modelIndex?: number;
  /**
   * Ocultos e isolados com que a árvore nasce. No federado a árvore desmonta ao
   * trocar de painel, e o motor continua com tudo aplicado: sem isto ela voltava
   * com os ícones zerados.
   */
  initialVisibility?: IBim3DVisibility;
}

/** Ocultos e nós isolados (com os ids de cada um), em ids locais ao modelo. */
export interface IBim3DVisibility {
  hidden: Set<number>;
  isolatedByKey: Map<string, number[]>;
}

const unionOf = (byKey: Map<string, number[]>): Set<number> => {
  const ids = new Set<number>();
  byKey.forEach((nodeIds) => nodeIds.forEach((id) => ids.add(id)));
  return ids;
};

/**
 * Estado das ferramentas do viewer 3D: árvore espacial, seleção (nos dois
 * sentidos), propriedades do elemento e plano de corte.
 *
 * O motor é a fonte da verdade da cena — aqui só guardamos o que a UI precisa
 * desenhar. Nada de duplicar geometria ou visibilidade no React.
 */
export const useBim3DTools = ({ handleRef, modelIndex, initialVisibility }: IUseBim3DToolsParams) => {
  const [tree, setTree] = useState<BimTreeNode[]>([]);
  // O data model chega DEPOIS do render (o server o grava em background), então a
  // árvore tem um estado de espera próprio — sem ele o painel só ficaria vazio,
  // sem o usuário saber se está carregando ou se o modelo não tem hierarquia.
  const [dataModelState, setDataModelState] = useState<'loading' | 'ready' | 'unavailable'>('loading');
  const [selection, setSelection] = useState<IBimSelection | null>(null);
  // Lista, não um só: com vários elementos selecionados o painel agrega (soma as
  // quantidades, mostra "*Varia*" no que difere) — mesmo comportamento do 2D.
  const [properties, setProperties] = useState<BimEntityProperties[]>([]);
  // As propriedades vêm do worker do data model e podem voltar fora de ordem: a
  // resposta de uma seleção antiga não pode sobrescrever a de uma mais nova, nem
  // repovoar um painel que já foi limpo.
  const propertiesRequest = useRef(0);
  const clearProperties = useCallback(() => {
    propertiesRequest.current++;
    setProperties([]);
  }, []);
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);
  const [section, setSectionState] = useState<BimSectionPlane>(DEFAULT_SECTION);
  const [isolated, setIsolated] = useState(() => (initialVisibility?.isolatedByKey.size ?? 0) > 0);
  // Nós isolados pelo filtro da árvore — é o que troca o ícone da linha. A cena
  // mostra a união deles.
  const [isolatedByKey, setIsolatedByKey] = useState<Map<string, number[]>>(
    () => new Map(initialVisibility?.isolatedByKey),
  );
  const isolatedKeys = useMemo(() => Array.from(isolatedByKey.keys()), [isolatedByKey]);
  // Ids que o isolamento deixa à vista, para o viewpoint gravar.
  const [isolatedIds, setIsolatedIds] = useState<Set<number>>(
    () => (initialVisibility ? unionOf(initialVisibility.isolatedByKey) : new Set()),
  );
  const [multiSelect, setMultiSelect] = useState(false);
  // Ghost do modelo pedido na toolbar (fica até desligar), o ghost aplicado a
  // uma seleção e o X-Ray temporário do foco / duplo clique (sai com a seleção).
  // A toolbar mostra os três como um botão só.
  const [ghostPinned, setGhostPinned] = useState(false);
  const [ghostedSelection, setGhostedSelection] = useState(false);
  const [focusGhost, setFocusGhost] = useState(false);
  // Espelho do que está oculto na cena. O motor continua sendo a fonte da
  // verdade do render; isto existe só para a árvore saber que ícone desenhar.
  const [hiddenIds, setHiddenIds] = useState<Set<number>>(() => new Set(initialVisibility?.hidden));
  const [measureMode, setMeasureModeState] = useState<BimMeasureMode>('none');
  const [measurements, setMeasurements] = useState<BimMeasurement[]>([]);
  const [projection, setProjectionState] = useState<BimProjectionMode>('perspective');

  const elementIndex = useRef<Map<number, string>>(new Map());
  const partOwner = useRef<Map<number, number>>(new Map());
  // Os ids de um nó não mudam enquanto a árvore for a mesma; recalcular a cada
  // clique no olho atravessaria a subárvore inteira de novo.
  const nodeIds = useRef<Map<string, number[]>>(new Map());

  // Chega quando o data model termina de baixar/decodificar — depois do render.
  const handleDataModelReady = useCallback((detail: { available: boolean }) => {
    if (!detail.available) { setDataModelState('unavailable'); return; }
    const nodes = handleRef.current?.getSpatialTree(modelIndex) ?? [];
    const index = indexElements(nodes);
    elementIndex.current = index.groupOf;
    partOwner.current = index.ownerOf;
    nodeIds.current = new Map();
    setTree(nodes);
    setDataModelState(nodes.length > 0 ? 'ready' : 'unavailable');
    // Raízes abertas: uma árvore toda fechada não comunica que há conteúdo.
    setExpandedKeys(nodes.map((node) => node.key));
  }, [handleRef, modelIndex]);

  const reset = useCallback(() => {
    setTree([]);
    setDataModelState('loading');
    setSelection(null);
    clearProperties();
    setExpandedKeys([]);
    setIsolated(false);
    setIsolatedByKey(new Map());
    setIsolatedIds(new Set());
    setMultiSelect(false);
    setGhostPinned(false);
    setGhostedSelection(false);
    setFocusGhost(false);
    setHiddenIds(new Set());
    setMeasureModeState('none');
    setMeasurements([]);
    setProjectionState('perspective');
    setSectionState(DEFAULT_SECTION);
    elementIndex.current = new Map();
    partOwner.current = new Map();
    nodeIds.current = new Map();
  }, []);

  /**
   * Carrega as propriedades de TODA a seleção — é o que permite o painel somar
   * quantidades e marcar o que varia, como no viewer 2D.
   *
   * O teto existe porque um nó de árvore pode cobrir milhares de elementos, e
   * agregar tudo isso trava a UI por um painel que ninguém leria linha a linha.
   */
  const loadProperties = useCallback(async (expressIds: number[]) => {
    const handle = handleRef.current;
    if (!handle || expressIds.length === 0) { clearProperties(); return; }

    const request = ++propertiesRequest.current;
    const list = await handle.getEntitiesProperties(
      expressIds.slice(0, MAX_AGGREGATED_PROPERTIES),
      modelIndex,
    );
    if (request === propertiesRequest.current) { setProperties(list); }
  }, [handleRef, modelIndex, clearProperties]);

  // 3D → árvore: revela o nó do elemento clicado e mostra suas propriedades.
  const handleSelectionChanged = useCallback((next: BimSelection) => {
    const toTreeKeys = (ids: number[]) =>
      ids.filter((id) => elementIndex.current.has(id)).map(elementTreeKey);

    if (next.expressId === null) {
      // Multi-seleção pode ter deselecionado o último clicado e ainda ter itens.
      const remaining = next.expressIds ?? [];
      setSelection(remaining.length > 0
        ? { ids: remaining, primaryId: null, treeKeys: toTreeKeys(remaining) }
        : null);
      clearProperties();
      return;
    }

    // Abre até o grupo de tipo; a folha do elemento nasce quando ele carrega.
    // Peça (camada de parede composta): abre também a folha do elemento dono.
    const groupKey = elementIndex.current.get(next.expressId);
    if (groupKey) {
      const owner = partOwner.current.get(next.expressId);
      setExpandedKeys((keys) => {
        const trail = [...pathTo(tree, groupKey), groupKey];
        if (owner !== undefined) { trail.push(elementTreeKey(owner)); }
        return Array.from(new Set(keys.concat(trail)));
      });
    }

    // `expressIds` é a seleção inteira (multi); `expressId` é o último clicado,
    // e é dele que o painel mostra as propriedades.
    const ids = next.expressIds?.length ? next.expressIds : [next.expressId];
    const primary = next.expressId;
    const treeKeys = toTreeKeys([primary, ...ids.filter((id) => id !== primary)]);
    setSelection({ ids, primaryId: primary, treeKeys });
    loadProperties(ids);
  }, [tree, loadProperties]);

  // Árvore → 3D: folha destaca e enquadra o elemento; contêiner enquadra o
  // conjunto (e alimenta isolar/ocultar com todos os seus elementos).
  const selectTreeNode = useCallback((node: BimTreeNode) => {
    const handle = handleRef.current;
    if (!handle) { return; }

    const isElement = node.kind === 'element';
    const ids = isElement ? elementIds(node) : collectIds(node);

    if (ids.length === 0) { return; }

    if (isElement && node.expressId !== undefined) {
      // Com peças, a geometria costuma estar só nelas: selecionar o elemento
      // sozinho não pintaria nada. O painel continua mostrando o elemento.
      if (ids.length > 1) { handle.selectEntities(ids, { frame: true, modelIndex }); }
      else { handle.select(node.expressId, { frame: true, modelIndex }); }
      setSelection({ ids, primaryId: node.expressId, treeKeys: [node.key] });
      loadProperties([node.expressId]);
      return;
    }

    // Contêiner: pinta e enquadra todos os elementos do nó.
    handle.selectEntities(ids, { frame: true, modelIndex });
    setSelection({ ids, primaryId: null, treeKeys: [node.key] });
    // Contêiner: sem elemento único, o painel agrega o conjunto (soma das
    // quantidades do pavimento / do grupo de tipo).
    loadProperties(ids);
  }, [handleRef, loadProperties, modelIndex]);

  // Seleção que não nasce de um clique (link direto): mesmo foco do duplo
  // clique — enquadra e deixa o resto em X-Ray, senão o elemento costuma ficar
  // escondido atrás de parede/laje.
  const selectElement = useCallback((expressId: number) => {
    const handle = handleRef.current;
    handle?.select(expressId, { modelIndex });
    handleSelectionChanged({ expressId, expressIds: [expressId], modelIndex: modelIndex ?? 0 });
    handle?.focusSelection();
    setFocusGhost(true);
  }, [handleRef, modelIndex, handleSelectionChanged]);

  const isolateSelection = useCallback(() => {
    const ids = selection?.ids;
    if (!ids || ids.length === 0) { return; }
    handleRef.current?.isolate(ids);
    setIsolated(true);
    setIsolatedByKey(new Map());
    setIsolatedIds(new Set(ids));
  }, [handleRef, selection]);

  const hideSelection = useCallback(() => {
    const ids = selection?.ids;
    if (!ids || ids.length === 0) { return; }
    handleRef.current?.hide(ids);
    setHiddenIds((current) => {
      const next = new Set(current);
      for (const id of ids) { next.add(id); }
      return next;
    });
    // O que sumiu não pode continuar selecionado (nem mantendo o X-Ray ligado
    // contra um alvo invisível).
    handleRef.current?.clearSelection();
    setSelection(null);
    clearProperties();
    setFocusGhost(false);
  }, [handleRef, selection]);

  /** Volta tudo ao sólido sem perder a seleção, os ocultos nem o isolamento. */
  const clearGhost = useCallback(() => {
    handleRef.current?.clearGhost();
    setGhostPinned(false);
    setGhostedSelection(false);
    setFocusGhost(false);
  }, [handleRef]);

  /**
   * Ghost na seleção: ela fica translúcida (e continua selecionada — o painel de
   * propriedades segue aberto). Um ghost por vez: aplicar este desfaz o outro.
   */
  const toggleGhostSelection = useCallback(() => {
    if (ghostedSelection) { clearGhost(); return; }
    const ids = selection?.ids;
    if (!ids?.length) { return; }
    handleRef.current?.clearGhost();
    handleRef.current?.ghost(ids);
    setGhostPinned(false);
    setFocusGhost(false);
    setGhostedSelection(true);
  }, [handleRef, selection, ghostedSelection, clearGhost]);

  /**
   * Ghost em volta: o que NÃO está selecionado fica translúcido (sem seleção, o
   * modelo inteiro). Selecionar alguns elementos e ligar isto os destaca no
   * contexto do resto.
   */
  /**
   * Botão "voltar ao normal" do grupo de visibilidade: desfaz ghost, isolamento e
   * ocultos de uma vez, mas mantém a seleção — diferente do Esc, que limpa tudo.
   */
  const restoreVisibility = useCallback(() => {
    handleRef.current?.showAll();
    clearGhost();
    setIsolated(false);
    setIsolatedByKey(new Map());
    setIsolatedIds(new Set());
    setHiddenIds(new Set());
  }, [handleRef, clearGhost]);

  const toggleGhostOthers = useCallback(() => {
    if (ghostPinned) { clearGhost(); return; }
    handleRef.current?.clearGhost();
    handleRef.current?.setGhostMode(true);
    setGhostedSelection(false);
    setFocusGhost(false);
    setGhostPinned(true);
  }, [handleRef, ghostPinned, clearGhost]);

  /**
   * O olho da árvore. Um nó só volta inteiro quando está inteiro oculto — no
   * estado parcial (alguém escondeu um filho lá dentro) o clique oculta o resto,
   * que é o que o usuário espera de um controle no pai.
   */
  const idsOfNode = useCallback((node: BimTreeNode): number[] => {
    let ids = nodeIds.current.get(node.key);
    if (!ids) {
      ids = node.kind === 'element' ? elementIds(node) : collectIds(node);
      nodeIds.current.set(node.key, ids);
    }
    return ids;
  }, []);

  const toggleNodeVisibility = useCallback((node: BimTreeNode) => {
    const handle = handleRef.current;
    if (!handle) { return; }

    const ids = idsOfNode(node);
    if (ids.length === 0) { return; }

    // Chamar o motor FORA do updater: em StrictMode o updater roda duas vezes, e
    // hide/show sairiam em dobro.
    const allHidden = ids.every((id) => hiddenIds.has(id));
    const next = new Set(hiddenIds);
    if (allHidden) {
      handle.show(ids, modelIndex);
      for (const id of ids) { next.delete(id); }
    } else {
      handle.hide(ids, modelIndex);
      for (const id of ids) { next.add(id); }
    }
    setHiddenIds(next);

    // Esconder o que está selecionado deixaria o painel de propriedades e o
    // X-Ray apontando pra um alvo que não está mais na tela.
    if (!allHidden && selection?.ids.some((id) => ids.includes(id))) {
      handle.clearSelection();
      setSelection(null);
      clearProperties();
      setFocusGhost(false);
    }
  }, [handleRef, selection, hiddenIds, idsOfNode, modelIndex]);

  /**
   * O filtro da árvore: soma o nó aos isolados (a cena mostra a união); no nó
   * já isolado, tira só ele. Como o olho, vale para vários nós ao mesmo tempo.
   */
  const toggleNodeIsolation = useCallback((node: BimTreeNode) => {
    const handle = handleRef.current;
    if (!handle) { return; }
    const removing = isolatedByKey.has(node.key);
    if (!removing && idsOfNode(node).length === 0) { return; }

    const next = new Map(isolatedByKey);
    if (removing) { next.delete(node.key); } else { next.set(node.key, idsOfNode(node)); }
    const ids = unionOf(next);

    if (ids.size === 0) {
      handle.isolate(null, modelIndex);
      setIsolated(false);
      setIsolatedByKey(new Map());
      setIsolatedIds(new Set());
      return;
    }
    handle.isolate(Array.from(ids), modelIndex);
    setIsolated(true);
    setIsolatedByKey(next);
    setIsolatedIds(ids);
  }, [handleRef, isolatedByKey, idsOfNode, modelIndex]);

  /**
   * Volta ao padrão: tudo à vista, nada selecionado, sem fantasma nem
   * isolamento. É o que o Esc faz na cena — aqui só espelhamos o resultado,
   * venha do teclado (`handleViewReset`) ou de quem chamar isto.
   */
  const handleViewReset = useCallback(() => {
    setIsolated(false);
    setIsolatedByKey(new Map());
    setIsolatedIds(new Set());
    setHiddenIds(new Set());
    setGhostPinned(false);
    setGhostedSelection(false);
    setFocusGhost(false);
    setSelection(null);
    clearProperties();
  }, []);

  const resetView = useCallback(() => {
    handleRef.current?.resetView();
    handleViewReset();
  }, [handleRef, handleViewReset]);

  /**
   * Alterna o modo de medição — clicar no modo ativo desliga. Enquanto mede, o
   * clique no 3D é ponto de medida, então a seleção corrente sai da frente
   * (senão o X-Ray e o painel ficam apontando pra um elemento que o usuário não
   * está mais mexendo).
   */
  const toggleMeasureMode = useCallback((mode: Exclude<BimMeasureMode, 'none'>) => {
    const next = measureMode === mode ? 'none' : mode;
    handleRef.current?.setMeasureMode(next);
    setMeasureModeState(next);
    if (next !== 'none') {
      handleRef.current?.clearSelection();
      setSelection(null);
      clearProperties();
      setFocusGhost(false);
    }
  }, [handleRef, measureMode]);

  /**
   * O motor é dono das medições (é ele quem desenha o overlay) e do modo — o Esc
   * na cena sai da ferramenta lá dentro. Aqui só espelhamos os dois.
   */
  const handleMeasureChanged = useCallback(
    (state: { mode: BimMeasureMode; measurements: BimMeasurement[] }) => {
      setMeasureModeState(state.mode);
      setMeasurements(state.measurements);
    },
    [],
  );

  // O estado vem do evento do motor (`handleProjectionChanged`), que também
  // dispara quando um viewpoint restaurado traz a projeção dele.
  const setProjection = useCallback((mode: BimProjectionMode) => {
    handleRef.current?.setProjection(mode);
  }, [handleRef]);

  const handleProjectionChanged = useCallback((mode: BimProjectionMode) => {
    setProjectionState(mode);
  }, []);

  const clearMeasurements = useCallback(() => {
    handleRef.current?.clearMeasurements();
  }, [handleRef]);

  const frameSelection = useCallback(() => {
    const ids = selection?.ids;
    if (!ids?.length) { return; }
    // Enquadrar sozinho costuma deixar o elemento atrás de parede/laje; o foco
    // enquadra E deixa o resto translúcido, que é o que mostra a peça de fato.
    if (selection?.primaryId !== null) { handleRef.current?.focusSelection(); }
    else { handleRef.current?.frameEntities(ids); }
    setFocusGhost(true);
  }, [handleRef, selection]);

  const toggleMultiSelect = useCallback(() => {
    setMultiSelect((current) => {
      handleRef.current?.setMultiSelect(!current);
      return !current;
    });
  }, [handleRef]);

  const clearSelection = useCallback(() => {
    handleRef.current?.clearSelection();
    setSelection(null);
    clearProperties();
    setFocusGhost(false);
  }, [handleRef]);

  // Corte: o motor aplica no frame seguinte, então arrastar o slider já corta
  // em tempo real — sem recarregar nem re-tesselar nada.
  const setSection = useCallback((next: Partial<BimSectionPlane>) => {
    setSectionState((current) => {
      const merged = { ...current, ...next };
      handleRef.current?.setSectionPlane(merged.enabled ? merged : null);
      return merged;
    });
  }, [handleRef]);

  // Viewpoint restaurado: o motor já cortou, aqui o painel só acompanha.
  const handleSectionChanged = useCallback((next: BimSectionPlane | null) => {
    setSectionState((current) => next ?? { ...current, enabled: false });
  }, []);

  const hasSelection = !!selection?.ids.length;

  // Recalcula só quando a visibilidade (ou a árvore) muda — não a cada render.
  const hiddenCountByKey = useMemo(
    () => (hiddenIds.size > 0 ? countHidden(tree, hiddenIds) : new Map<string, number>()),
    [tree, hiddenIds],
  );

  return useMemo(() => ({
    tree,
    dataModelState,
    dataModelReady: dataModelState === 'ready',
    expandedKeys,
    setExpandedKeys,
    selection,
    hasSelection,
    selectionCount: selection?.ids.length ?? 0,
    properties,
    section,
    isolated,
    isolatedKeys,
    isolatedByKey,
    isolatedIds,
    multiSelect,
    ghost: ghostPinned || ghostedSelection || focusGhost,
    ghostSelectionActive: ghostedSelection,
    ghostOthersActive: ghostPinned || focusGhost,
    visibilityChanged: ghostPinned || ghostedSelection || focusGhost || isolated || hiddenIds.size > 0,
    hiddenIds,
    hiddenCountByKey,
    hasHidden: hiddenIds.size > 0,
    measureMode,
    measurements,
    toggleMeasureMode,
    handleMeasureChanged,
    clearMeasurements,
    projection,
    setProjection,
    handleProjectionChanged,
    handleDataModelReady,
    handleSelectionChanged,
    selectTreeNode,
    selectElement,
    isolateSelection,
    hideSelection,
    toggleNodeVisibility,
    toggleNodeIsolation,
    resetView,
    handleViewReset,
    frameSelection,
    toggleMultiSelect,
    toggleGhostSelection,
    toggleGhostOthers,
    restoreVisibility,
    clearSelection,
    setSection,
    handleSectionChanged,
    reset,
  }), [
    tree, dataModelState, expandedKeys, selection, hasSelection, properties, section, isolated, isolatedKeys,
    isolatedByKey, isolatedIds, multiSelect, ghostPinned, ghostedSelection, focusGhost, hiddenIds, hiddenCountByKey, measureMode, measurements,
    toggleMeasureMode, handleMeasureChanged, clearMeasurements, projection, setProjection,
    handleProjectionChanged, handleDataModelReady,
    handleSelectionChanged, selectTreeNode, selectElement, isolateSelection, hideSelection, handleViewReset,
    toggleNodeVisibility, toggleNodeIsolation, resetView, frameSelection, toggleMultiSelect, toggleGhostSelection, toggleGhostOthers, restoreVisibility, clearSelection,
    setSection, handleSectionChanged, reset,
  ]);
};

export type Bim3DTools = ReturnType<typeof useBim3DTools>;
